import { createFileRoute } from "@tanstack/react-router";
import { CodeExampleTabs } from "@/components/CodeExampleTabs";

/** Route Setup */
export const Route = createFileRoute("/_app/api-agent-doc-new")({
  component: RouteComponent,
});

/** Utility Components */
const Section: React.FC<{
  id?: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, title, children }) => (
  <section id={id} className="mb-12 scroll-mt-20">
    <h2 className="mb-6 border-b-2 border-blue-200 pb-3 text-3xl font-bold text-gray-900 dark:border-blue-800 dark:text-white">
      {title}
    </h2>
    {children}
  </section>
);

const SubSection: React.FC<{
  id?: string;
  title: string;
  children: React.ReactNode;
  level?: number;
}> = ({ id, title, children, level = 3 }) => (
  <div id={id} className="mb-8 scroll-mt-16">
    <h3
      className={`mb-4 font-bold text-gray-800 dark:text-gray-200 ${level === 3 ? `text-xl` : `text-lg`}`}
    >
      {title}
    </h3>
    {children}
  </div>
);

const CodeBlock: React.FC<{ children: React.ReactNode; language?: string }> = ({
  children,
  language,
}) => (
  <div className="mb-4">
    {language && (
      <div className="rounded-t border-b border-gray-700 bg-gray-800 px-3 py-1 font-mono text-xs text-gray-200">
        {language}
      </div>
    )}
    <pre
      className={`overflow-x-auto rounded ${language ? `rounded-t-none` : ``} border border-gray-200 bg-gray-50 p-4 font-mono text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200`}
    >
      {children}
    </pre>
  </div>
);

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code className="rounded border border-gray-300 bg-gray-200 px-2 py-1 font-mono text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
    {children}
  </code>
);

const EndpointCard: React.FC<{
  method: string;
  url: string;
  children: React.ReactNode;
}> = ({ method, url, children }) => (
  <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div className="mb-4 flex items-center gap-3">
      <span
        className={`rounded-full px-3 py-1 text-xs font-bold text-white ${
          method === "GET"
            ? "bg-green-500"
            : method === "POST"
              ? "bg-blue-500"
              : method === "PUT"
                ? "bg-yellow-500"
                : method === "DELETE"
                  ? "bg-red-500"
                  : "bg-gray-500"
        }`}
      >
        {method}
      </span>
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-700 dark:bg-gray-700 dark:text-gray-300">
        {url}
      </code>
    </div>
    {children}
  </div>
);

const ResponseExample: React.FC<{
  status: string;
  response: string;
  isSuccess?: boolean;
}> = ({ status, response, isSuccess = false }) => (
  <div className="mb-4">
    <div className="mb-2 flex items-center gap-2">
      <span
        className={`w-16 rounded py-1 text-center text-xs font-bold ${
          isSuccess
            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
        }`}
      >
        {status}
      </span>
      <span className="text-sm text-gray-600 dark:text-gray-400">Response</span>
    </div>
    <CodeBlock language="json">{response}</CodeBlock>
  </div>
);

/** Main Page */
function RouteComponent() {
  return (
    <div className="min-h-screen">
      <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Title */}
        <header className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold text-gray-900 dark:text-white">
            API Documentation
          </h1>
        </header>

        {/* TOC */}
        <nav
          aria-label="Table of contents"
          className="mb-12 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900"
        >
          <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
            Contents
          </h2>
          <ul className="columns-3 gap-8 space-y-2 text-sm">
            <li>
              <a href="#introduction" className="toc-link">
                1. Introduction
              </a>
            </li>
            <li>
              <a href="#authentication" className="toc-link">
                2. Authentication
              </a>
            </li>
            <li>
              <a href="#base-url" className="toc-link">
                3. Base URL
              </a>
            </li>
            <li>
              <a href="#endpoints" className="toc-link break-before-column">
                4. Endpoints
              </a>
            </li>
            <li className="pl-4">
              <a href="#ai-human-detect" className="toc-link">
                4.1. AI or Human Text Detection
              </a>
            </li>
            <li className="pl-4">
              <a href="#source-detection" className="toc-link">
                4.2. Text Source Detection
              </a>
            </li>
            <li className="pl-4">
              <a href="#writing-analysis" className="toc-link">
                4.3. Writing Analysis
              </a>
            </li>
            <li className="pl-4">
              <a href="#humanized-text-detection" className="toc-link">
                4.4. Humanized Text Detection
              </a>
            </li>
            <li className="pl-4">
              <a href="#plagiarism" className="toc-link">
                4.5. Plagiarism Text
              </a>
            </li>
            <li className="pl-4">
              <a href="#text-highlighting" className="toc-link">
                4.6. Text Highlighting
              </a>
            </li>
            <li className="pl-4">
              <a href="#humanize" className="toc-link">
                4.7. Humanize
              </a>
            </li>
            <li className="pl-4">
              <a href="#pii-text-analyze" className="toc-link">
                4.8. PII Text Analyze
              </a>
            </li>
            <li className="pl-4">
              <a href="#available-entities" className="toc-link">
                4.9. Full List of Entities
              </a>
            </li>
            <li className="pl-4">
              <a href="#deepfake-audio" className="toc-link">
                4.10. DeepFake Audio Detection
              </a>
            </li>
            <li>
              <a
                href="#support-contact"
                className="toc-link break-before-column"
              >
                5. Support Contact
              </a>
            </li>
          </ul>
        </nav>

        {/* 1. Introduction */}
        <Section id="introduction" title="1. Introduction">
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            The AI Detection API provides a robust set of tools for analyzing
            text content to ensure authenticity, assess quality, and verify
            originality. Designed for flawless integration, this API enables
            developers to detect AI-generated content, evaluate plagiarism,
            analyze writing patterns, and identify text modifications.
          </p>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            Key functionalities include:
          </p>
          <ul className="ml-6 list-disc text-gray-700 dark:text-gray-300">
            <li>Detection of AI-generated and human-authored text.</li>
            <li>
              Comprehensive writing quality metrics, such as readability and
              vocabulary richness.
            </li>
            <li>
              Identification of potential plagiarism with detailed source
              analysis.
            </li>
            <li>Detection of paraphrased or altered content.</li>
            <li>
              Sentence-level AI probability highlighting for in-depth analysis.
            </li>
          </ul>
          <p className="mt-4 text-gray-700 dark:text-gray-300">
            This document outlines the API's authentication process, available
            endpoints, and request and response structures, to help developers
            effectively integrate its capabilities into their applications.
          </p>
        </Section>

        {/* 2. Authentication */}
        <Section id="authentication" title="2. Authentication">
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            The API uses an API Key for authentication, which must be included
            in the request header as the Authorization key. The server will
            validate the API Key with each request to authorize access.
          </p>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            Include the API Key in the request header as follows:
          </p>
          <ul className="mb-3 ml-6 list-disc text-gray-700 dark:text-gray-300">
            <li>
              Header: <Kbd>API_Key</Kbd>
            </li>
            <li>
              Value: <Kbd>Bearer {"{YOUR API KEY}"}</Kbd>
            </li>
          </ul>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            Your API Key is unique to your account. Sharing it with others could
            compromise your access.
          </p>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            Treat the API Key like a password. Avoid exposing it in public
            repositories, client-side code, or unprotected environments.
          </p>
        </Section>

        {/* 3. Base URL */}
        <Section id="base-url" title="3. Base URL">
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            The Base URL serves as the root address for all API requests. To
            make a request to any endpoint, clients will append the endpoint
            path to this Base URL.
          </p>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            <strong>BASE URL:</strong> https://api.polygraf.ai/integration/v1
          </p>
        </Section>

        {/* 4. Endpoints */}
        <Section id="endpoints" title="4. Endpoints">
          {/* 4.1 */}
          <SubSection
            id="ai-human-detect"
            title="4.1. AI or Human Text Detection Endpoint"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Analyzes input text and returns probability scores indicating
              whether the content was likely generated by AI or written by a
              human. Returns a JSON object containing confidence scores for both
              AI and human authorship.
            </p>

            <EndpointCard method="POST" url="/ai-detection">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Body
                  </h4>
                  <CodeBlock language="json">{`{
  "text": "string - The text content to analyze for AI detection"
}`}</CodeBlock>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`{
  "AI": float,
  "HUMAN": float,
  "cognitive_flow_index": float
}`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have enough balance!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have active subscription!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have access for this service."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeExampleTabs
                    endpoint="/ai-detection"
                    method="POST"
                    requestBody='{"text": "This is sample text for AI detection analysis."}'
                    apiKey="<API-Key>"
                    CodeBlock={CodeBlock}
                  />
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.2 */}
          <SubSection
            id="source-detection"
            title="4.2. Text Source Detection Endpoint"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Analyzes input text to identify whether it was written by a human
              or generated by specific AI models. Returns a JSON object
              containing probability scores for human authorship and various AI
              models, with each score indicating the likelihood of that source
              being responsible for generating the text.
            </p>

            <EndpointCard method="POST" url="/source-detection">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Body
                  </h4>
                  <CodeBlock language="json">{`{
  "text": "string - The text content to analyze for source detection"
}`}</CodeBlock>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`{
  "OPEN_AI": float,
  "MISTRAL": float,
  "CLAUDE": float,
  "GEMINI": float,
  "GRAMMAR_ENHANCED": float,
  "HUMAN": float,
  "cognitive_flow_index": float
}`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have enough balance!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have active subscription!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have access for this service."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeExampleTabs
                    endpoint="/source-detection"
                    method="POST"
                    requestBody='{"text": "This is sample text for source detection analysis."}'
                    apiKey="<API-Key>"
                    CodeBlock={CodeBlock}
                  />
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.3 */}
          <SubSection
            id="writing-analysis"
            title="4.3. Writing Analysis Endpoint"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Performs comprehensive text analysis and returns key writing
              quality metrics including readability, vocabulary richness, and
              sentence complexity. Returns numerical scores for six different
              writing characteristics to help evaluate the text's structural and
              stylistic properties.
            </p>

            <EndpointCard method="POST" url="/writing-analysis">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Body
                  </h4>
                  <CodeBlock language="json">{`{
  "text": "string - The text content to analyze for writing quality"
}`}</CodeBlock>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`{
  "readability": float,
  "syntactic_tree_depth": float,
  "vocabulary_richness": float,
  "perplexity": float,
  "average_sentence_length": float,
  "average_word_length": float,
  "cognitive_flow_index": float
}`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have enough balance!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have active subscription!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have access for this service."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeExampleTabs
                    endpoint="/writing-analysis"
                    method="POST"
                    requestBody='{"text": "This is sample text for writing quality analysis."}'
                    apiKey="<API-Key>"
                    CodeBlock={CodeBlock}
                  />
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.4 */}
          <SubSection
            id="humanized-text-detection"
            title="4.4. Humanized Text Detection Endpoint"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Detects whether input text has been paraphrased or modified in an
              attempt to evade AI detection systems. Returns probability scores
              indicating if the text is in its original form or has been
              deliberately altered.
            </p>

            <EndpointCard method="POST" url="/humanized-text-detection">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Body
                  </h4>
                  <CodeBlock language="json">{`{
  "text": "string - The text content to analyze for humanization detection"
}`}</CodeBlock>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`{
  "Altered": float,
  "Original": float
}`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have enough balance!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have active subscription!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have access for this service."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeExampleTabs
                    endpoint="/humanized-text-detection"
                    method="POST"
                    requestBody='{"text": "This is sample text for humanization detection analysis."}'
                    apiKey="<API-Key>"
                    CodeBlock={CodeBlock}
                  />
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.5 */}
          <SubSection id="plagiarism" title="4.5. Plagiarism Text Endpoint">
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Analyzes input text against online sources to detect potential
              plagiarism and content similarity. Returns detailed results
              including text chunks with similarity scores, source URLs with
              matched content, and category-wise similarity percentages across
              different types of sources like wikis, news sites, and academic
              publications.
            </p>

            <EndpointCard method="POST" url="/copyright-detection">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Body
                  </h4>
                  <CodeBlock language="json">{`{
  "text": "string - The text content to analyze for plagiarism detection"
}`}</CodeBlock>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`{
  "chunks": [
       { 
           "chunk": string,
           "idx": integer,
           "color": string(hexadecimal color code),
           "score": float
       }
   ],
  "category_averages": {
       "Wikihost": float,
       "Overall Similarity": float,
       "Student Publications": float,
       "Official News": float,
       "Online Learning": float,
       "Government Official": float,
       "Publications": float
   },
   "sources": [
      {
      "url": string,
      "url_category": string,
      "score": float,
      "idx": integer,
      "matched_content": string,
      "color": string(hexadecimal color code)
      }
   ]
}`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have enough balance!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have active subscription!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have access for this service."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeExampleTabs
                    endpoint="/copyright-detection"
                    method="POST"
                    requestBody='{"text": "This is sample text for plagiarism detection analysis."}'
                    apiKey="<API-Key>"
                    CodeBlock={CodeBlock}
                  />
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.6 */}
          <SubSection
            id="text-highlighting"
            title="4.6. Text Highlighting Endpoint"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Analyzes text to identify potential AI-generated content and
              returns both visual indicators and probability scores for each
              sentence. Returns a detailed breakdown with highlighting
              suggestions to visually distinguish different levels of AI
              probability, along with precise sentence-level scores and an
              overall AI probability score for the entire text.
            </p>

            <EndpointCard method="POST" url="/ai-detection-highlight">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Body
                  </h4>
                  <CodeBlock language="json">{`{
  "text": "string - The text content to analyze for highlighting"
}`}</CodeBlock>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`[
    {
    "sentence": string,
    "score": float,
    "paragraph_idx": integer,
    "color": string(hexadecimal color code)
    }
]`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have enough balance!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have active subscription!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have access for this service."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeExampleTabs
                    endpoint="/ai-detection-highlight"
                    method="POST"
                    requestBody='{"text": "This is sample text for highlighting analysis."}'
                    apiKey="<API-Key>"
                    CodeBlock={CodeBlock}
                  />
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.7 */}
          <SubSection id="humanize" title="4.7. Humanize Endpoint">
            <EndpointCard method="POST" url="/humanize">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Body
                  </h4>
                  <CodeBlock language="json">{`{
  "text": "string - The text content to humanize",
  "temperature": "float - Controls randomness (0.0 to 1.0)",
  "top_k": "float - Limits vocabulary selection",
  "top_p": "float - Controls nucleus sampling"
}`}</CodeBlock>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`{
  "text": "string - The humanized text output"
}`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have enough balance!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have active subscription!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have access for this service."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeExampleTabs
                    endpoint="/humanize"
                    method="POST"
                    requestBody='{"text": "This is sample text to humanize.", "temperature": 0.7, "top_k": 50, "top_p": 0.9}'
                    apiKey="<API-Key>"
                    CodeBlock={CodeBlock}
                  />
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.8 */}
          <SubSection id="pii-text-analyze" title="4.8. PII Text analyzing">
            <EndpointCard method="POST" url="/pii/text-analyze">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Body
                  </h4>
                  <CodeBlock language="json">{`{
  "text": "string - The text content to analyze for PII",
  "entity_list": "array[str,str,...] - List of entity types to detect",
  "detect_pid": "boolean - Whether to detect personal identifiers"
}`}</CodeBlock>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`[
  {
    "entity_type": "string",
    "entity_text": "string",
    "start": int,
    "end": int,
    "score": float
  }
]`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have enough balance!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have active subscription!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have access for this service."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeExampleTabs
                    endpoint="/pii/text-analyze"
                    method="POST"
                    requestBody='{"text": "This is sample text with PII like John Doe and john@example.com", "entity_list": ["PERSON", "EMAIL", "PHONE_NUMBER"], "detect_pid": true}'
                    apiKey="<API-Key>"
                    CodeBlock={CodeBlock}
                  />
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.9 */}
          <SubSection
            id="available-entities"
            title="4.9. Full List of Entities"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Returns the full list of supported entities.
            </p>

            <EndpointCard method="GET" url="/available-entities">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`[
  "PERSON",
  "EMAIL",
  "PHONE_NUMBER",
  "CREDIT_CARD",
  "SSN",
  "ADDRESS"
]`}
                    isSuccess={true}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeExampleTabs
                    endpoint="/available-entities"
                    method="GET"
                    apiKey="<API-Key>"
                    CodeBlock={CodeBlock}
                  />
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.10 */}
          <SubSection
            id="deepfake-audio"
            title="4.10. DeepFake audio Detection"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Processes the input file and returns the binary data of the
              redacted document. Automatically mask sensitive data in documents,
              reports, and filesystems.
            </p>

            <EndpointCard method="POST" url="/pii/audio-analysis">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>multipart/form-data</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Form Data
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="font-semibold">Field</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>reference_file</Kbd>
                      </div>
                      <div>Reference audio file for comparison</div>
                      <div>
                        <Kbd>candidate_file</Kbd>
                      </div>
                      <div>Audio file to analyze</div>
                      <div>
                        <Kbd>chunk_size</Kbd>
                      </div>
                      <div>Size of audio chunks (optional)</div>
                      <div>
                        <Kbd>overlap</Kbd>
                      </div>
                      <div>Overlap between chunks (optional)</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`[
  {
    "chunk_id": "string",
    "time_range": "string",
    "start_time": int,
    "end_time": int,
    "identity": {
      "result": "string",
      "confidence": float,
      "same_person_prob": float,
      "different_person_prob": float
    },
    "authenticity": {
      "result": "string",
      "confidence": float,
      "real_prob": float,
      "fake_prob": float
    }
  }
]`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have enough balance!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have active subscription!"}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"You do not have access for this service."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeExampleTabs
                    endpoint="/pii/audio-analysis"
                    method="POST"
                    apiKey="<API-Key>"
                    isMultipart={true}
                    formFields={[
                      {
                        name: "reference_file",
                        description: "Reference audio file for comparison",
                      },
                      {
                        name: "candidate_file",
                        description: "Audio file to analyze",
                      },
                      {
                        name: "chunk_size",
                        description: "Size of audio chunks (optional)",
                      },
                      {
                        name: "overlap",
                        description: "Overlap between chunks (optional)",
                      },
                    ]}
                    CodeBlock={CodeBlock}
                  />
                </div>
              </div>
            </EndpointCard>
          </SubSection>
        </Section>

        {/* 5. Support Contact */}
        <Section id="support-contact" title="5. Support Contact">
          <p className="mb-4 text-gray-700 dark:text-gray-300">
            For further help, contact:
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            Email: vali@polygraf.ai
          </p>
        </Section>
      </div>
    </div>
  );
}
